import React, { useEffect, useState, useContext, useMemo } from 'react';
import { Box, Paper, Typography, Stack, Button, Select, MenuItem, TextField, Chip, IconButton, Tooltip, ToggleButtonGroup, ToggleButton, Slider, Popover, Divider, FormControl, InputLabel } from '@mui/material';
import { Add, Remove, Save, ContentCopy, DeleteOutline, EditOutlined } from '@mui/icons-material';
import { API_URL } from '../utils/apiConfig';
import { AuthContext } from '../context/AuthContext';

const DAYS = [
  { id: 1, name: 'Mon' },
  { id: 2, name: 'Tue' },
  { id: 3, name: 'Wed' },
  { id: 4, name: 'Thu' },
  { id: 5, name: 'Fri' },
  { id: 6, name: 'Sat' },
  { id: 0, name: 'Sun' }
];

const DEFAULT_ROLES = ['Manager', 'Shift Lead', 'Barista', 'Server', 'Kitchen', 'Bartender / Server', 'Barback', 'Cashier'];

export default function RequirementsBoard({ effectiveFrom, onSaved, selectedLocation = 'all' }) {
  const { user } = useContext(AuthContext) || {};
  const [roles, setRoles] = useState(DEFAULT_ROLES);
  const [grid, setGrid] = useState({}); // { [role]: { [dayId]: [{id?, startTime, endTime, headcount}] } }
  // Snapshot of what exists on the server to compute deletes/updates
  const [serverSnapshot, setServerSnapshot] = useState({}); // same shape as grid but items include id
  const [saving, setSaving] = useState(false);
  const [newRole, setNewRole] = useState('');
  const [trackHeight, setTrackHeight] = useState(64);
  const [hasBulk, setHasBulk] = useState(() => {
    try {
      const v = localStorage.getItem('dhr_req_bulk_supported');
      return v === null ? true : v === 'true';
    } catch {
      return true;
    }
  });
  const [createPopover, setCreatePopover] = useState({ open: false, anchorEl: null, role: null, dayId: null, start: '09:00', end: '17:00', headcount: 1 });
  const [editPopover, setEditPopover] = useState({ open: false, anchorEl: null, role: null, dayId: null, idx: null, start: '09:00', end: '17:00', headcount: 1 });

  useEffect(() => {
    // Ensure grid has keys for all roles/days without wiping existing items
    setGrid(prev => {
      const next = { ...prev };
      roles.forEach(r => {
        if (!next[r]) next[r] = {};
        DAYS.forEach(d => { if (!next[r][d.id]) next[r][d.id] = []; });
      });
      return next;
    });
  }, [roles]);

  // Load existing requirements to pre-populate
  useEffect(() => {
    const loadExisting = async () => {
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams({ effectiveOn: effectiveFrom || new Date().toISOString().slice(0,10) });
        // Only add locationId filter if a specific location is selected
        if (selectedLocation !== 'all') {
          params.set('locationId', String(selectedLocation));
        }
        const resp = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/requirements?${params}`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await resp.json();
        if (!resp.ok) return;
        const items = data.data || [];
        const roleSet = new Set(roles);
        items.forEach(it => roleSet.add(it.role));
        let nextRoles = Array.from(roleSet);
        try {
          const storageKey = selectedLocation === 'all' ? 'dhr_req_roles_all' : `dhr_req_roles_${selectedLocation}`;
          const saved = JSON.parse(localStorage.getItem(storageKey) || '[]');
          if (Array.isArray(saved) && saved.length) {
            nextRoles.sort((a,b)=> saved.indexOf(a) - saved.indexOf(b));
          }
        } catch {}
        const g = {};
        const snap = {};
        nextRoles.forEach(r => { g[r] = {}; snap[r] = {}; DAYS.forEach(d => { g[r][d.id] = []; snap[r][d.id] = []; }); });
        items.forEach(it => {
          const r = it.role; const d = Number(it.day_of_week);
          if (!g[r]) { g[r] = {}; DAYS.forEach(dd=> g[r][dd.id] = []); }
          if (!snap[r]) { snap[r] = {}; DAYS.forEach(dd=> snap[r][dd.id] = []); }
          if (!g[r][d]) g[r][d] = [];
          if (!snap[r][d]) snap[r][d] = [];
          const obj = { id: it.id, startTime: it.start_time?.slice(0,5) || it.start_time, endTime: it.end_time?.slice(0,5) || it.end_time, headcount: it.headcount || 1, locationId: it.location_id };
          g[r][d].push({ ...obj });
          snap[r][d].push({ ...obj });
        });
        setRoles(nextRoles);
        setGrid(g);
        setServerSnapshot(snap);
      } catch {}
    };
    loadExisting();
  }, [selectedLocation, effectiveFrom]);

  const addBlock = (role, dayId) => {
    setGrid(prev => ({
      ...prev,
      [role]: {
        ...(prev[role] || {}),
        [dayId]: [ ...(prev[role]?.[dayId] || []), { id: null, startTime: '09:00', endTime: '17:00', headcount: 1 } ]
      }
    }));
  };

  const updateBlock = (role, dayId, idx, field, value) => {
    setGrid(prev => ({
      ...prev,
      [role]: {
        ...(prev[role] || {}),
        [dayId]: (prev[role]?.[dayId] || []).map((b,i)=> i===idx ? { ...b, [field]: value } : b)
      }
    }));
  };

  const removeBlock = (role, dayId, idx) => {
    setGrid(prev => ({
      ...prev,
      [role]: {
        ...(prev[role] || {}),
        [dayId]: (prev[role]?.[dayId] || []).filter((_,i)=> i!==idx)
      }
    }));
  };

  const addRole = (roleName) => {
    const name = String(roleName || '').trim();
    if (!name) return;
    if (roles.includes(name)) return;
    const nextRoles = [...roles, name];
    setRoles(nextRoles);
    setGrid(prev => {
      const next = { ...prev };
      next[name] = next[name] || {};
      DAYS.forEach(d => { next[name][d.id] = next[name][d.id] || []; });
      return next;
    });
    setNewRole('');
  };

  const removeRole = (roleName) => {
    setRoles(prev => prev.filter(r => r !== roleName));
    setGrid(prev => {
      const next = { ...prev };
      delete next[roleName];
      return next;
    });
  };

  const moveRole = (roleName, dir) => {
    setRoles(prev => {
      const idx = prev.indexOf(roleName); if (idx === -1) return prev;
      const to = Math.max(0, Math.min(prev.length - 1, idx + dir));
      const next = [...prev];
      const [r] = next.splice(idx,1);
      next.splice(to,0,r);
      const storageKey = selectedLocation === 'all' ? 'dhr_req_roles_all' : `dhr_req_roles_${selectedLocation}`;
      try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const ratioToTime = (ratio) => {
    const totalMin = Math.round(Math.max(0, Math.min(1, ratio)) * 24 * 60 / 15) * 15;
    const hh = Math.floor(totalMin / 60); const mm = totalMin % 60;
    const pad = (n)=> String(n).padStart(2,'0');
    return `${pad(hh)}:${pad(mm)}`;
  };

  // Helpers for drag computations
  const timeToMinutes = (t) => { if (!t) return 0; const [h,m] = String(t).split(':').map(Number); return (h||0)*60 + (m||0); };
  const minutesToTime = (m) => { m = Math.max(0, Math.min(24*60-1, m)); const hh = String(Math.floor(m/60)).padStart(2,'0'); const mm = String(m%60).padStart(2,'0'); return `${hh}:${mm}`; };
  const to12h = (t) => {
    if (!t) return '';
    const [H, M] = String(t).split(':').map(Number);
    let h = H % 12; if (h === 0) h = 12;
    const ampm = H < 12 ? 'AM' : 'PM';
    return `${h}:${String(M||0).padStart(2,'0')} ${ampm}`;
  };
  const to12hCompact = (t) => {
    if (!t) return '';
    const [H, M] = String(t).split(':').map(Number);
    let h = H % 12; if (h === 0) h = 12;
    const mm = Number(M||0);
    const ap = H < 12 ? 'a' : 'p';
    return mm === 0 ? `${h}${ap}` : `${h}:${String(mm).padStart(2,'0')}${ap}`;
  };
  const updateBlockTimes = (prev, role, dayId, idx, startTime, endTime) => {
    const arr = prev[role][dayId] || [];
    const nextArr = arr.map((b,i)=> i===idx ? { ...b, startTime, endTime } : b);
    return { ...prev, [role]: { ...prev[role], [dayId]: nextArr } };
  };

  const copyAcrossWeek = (role, dayId, idx) => {
    setGrid(prev => {
      const src = prev[role]?.[dayId]?.[idx];
      if (!src) return prev;
      const next = { ...prev };
      DAYS.forEach(d => {
        next[role][d.id] = [ ...(next[role][d.id] || []), { ...src } ];
      });
      return next;
    });
  };

  // Compute create/update/delete operations vs server snapshot
  const computeOps = () => {
    const currentIds = new Set();
    const creates = [];
    const updates = [];
    const deletes = [];
    const effFrom = effectiveFrom || new Date().toISOString().slice(0,10);

    // Prepare creates and updates, and collect current ids
    Object.entries(grid).forEach(([role, days]) => {
      Object.entries(days).forEach(([dayId, blocks]) => {
        (blocks || []).forEach(b => {
          if (b.id) {
            currentIds.add(b.id);
            updates.push({ id: b.id, role, day_of_week: Number(dayId), start_time: b.startTime, end_time: b.endTime, headcount: Number(b.headcount) || 1 });
          } else {
            const locId = b.locationId || (selectedLocation === 'all' ? (user?.store || 1) : selectedLocation);
            creates.push({ location_id: locId, role, day_of_week: Number(dayId), start_time: b.startTime, end_time: b.endTime, headcount: Number(b.headcount)||1, effective_from: effFrom, effective_to: null });
          }
        });
      });
    });

    // Determine deletes by diffing snapshot ids against currentIds
    Object.entries(serverSnapshot).forEach(([role, days]) => {
      Object.entries(days).forEach(([dayId, blocks]) => {
        (blocks || []).forEach(b => { if (b.id && !currentIds.has(b.id)) deletes.push(b.id); });
      });
    });

    return { creates, updates, deletes };
  };

  const saveAll = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const { creates, updates, deletes } = computeOps();

      // Run deletes, then updates, then creates
      let delFail = 0; let updFail = 0; let crtFail = 0; let firstError = null;
      for (const id of deletes) {
        const r = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/requirements/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) { delFail++; try { const er = await r.json(); if (!firstError) firstError = er.error; } catch {} }
      }
      for (const it of updates) {
        const { id, ...body } = it;
        const r = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/requirements/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body) });
        if (!r.ok) { updFail++; try { const er = await r.json(); if (!firstError) firstError = er.error; } catch {} }
      }
      for (const it of creates) {
        const r = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/requirements`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(it) });
        if (!r.ok) { crtFail++; try { const er = await r.json(); if (!firstError) firstError = er.error; } catch {} }
      }

      if (delFail + updFail + crtFail > 0) throw new Error(firstError || 'Failed to save some requirements');

      // Refresh from server to ensure UI reflects persisted state and capture IDs for new items
      const params = new URLSearchParams({ effectiveOn: effectiveFrom || new Date().toISOString().slice(0,10) });
      if (selectedLocation !== 'all') {
        params.set('locationId', String(selectedLocation));
      }
      const fresh = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/requirements?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      const freshData = await fresh.json();
      if (fresh.ok) {
        const items = freshData.data || [];
        const roleSet = new Set(roles);
        items.forEach(it => roleSet.add(it.role));
        const nextRoles = Array.from(roleSet);
        const g = {}; const snap = {};
        nextRoles.forEach(r => { g[r] = {}; snap[r] = {}; DAYS.forEach(d => { g[r][d.id] = []; snap[r][d.id] = []; }); });
        items.forEach(it => {
          const r = it.role; const d = Number(it.day_of_week);
          if (!g[r]) { g[r] = {}; DAYS.forEach(dd=> g[r][dd.id] = []); }
          if (!snap[r]) { snap[r] = {}; DAYS.forEach(dd=> snap[r][dd.id] = []); }
          if (!g[r][d]) g[r][d] = [];
          if (!snap[r][d]) snap[r][d] = [];
          const obj = { id: it.id, startTime: it.start_time?.slice(0,5) || it.start_time, endTime: it.end_time?.slice(0,5) || it.end_time, headcount: it.headcount || 1, locationId: it.location_id };
          g[r][d].push({ ...obj });
          snap[r][d].push({ ...obj });
        });
        setRoles(nextRoles);
        setGrid(g);
        setServerSnapshot(snap);
      }
      onSaved?.({ ok: true });
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  // --- Live HUD summary ---
  const durationMinutes = (start, end) => {
    const s = timeToMinutes(start); const e = timeToMinutes(end);
    return (e <= s ? e + 24*60 : e) - s;
  };
  const hudSummary = useMemo(() => {
    const byRole = {};
    roles.forEach(role => {
      let weeklyMinutes = 0;
      const perDay = {};
      DAYS.forEach(d => {
        const blocks = grid[role]?.[d.id] || [];
        let heads = 0; let mins = 0;
        blocks.forEach(b => {
          const dur = durationMinutes(b.startTime, b.endTime);
          heads += Number(b.headcount||1);
          mins += dur * (Number(b.headcount||1));
        });
        if (heads > 0 || mins > 0) perDay[d.id] = { heads, mins };
        weeklyMinutes += mins;
      });
      byRole[role] = { weeklyMinutes, perDay };
    });
    return byRole;
  }, [grid, roles]);

  // 12-hour time options for selects (30-minute steps)
  const TIME_OPTIONS = useMemo(() => {
    const out = [];
    for (let m=0; m<24*60; m+=30) {
      const val = minutesToTime(m);
      out.push({ value: val, label: to12h(val) });
    }
    return out;
  }, []);

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Requirements Board</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
          <TextField size="small" label="Add Role" placeholder="e.g., Barista" value={newRole} onChange={(e)=>setNewRole(e.target.value)} sx={{ minWidth: 180 }} />
          <Button size="small" variant="outlined" onClick={()=>addRole(newRole)}>Add Role</Button>
          <Select size="small" displayEmpty value="" onChange={(e)=>addRole(e.target.value)} sx={{ minWidth: 180 }}>
            <MenuItem value=""><em>Quick add from defaults</em></MenuItem>
            {DEFAULT_ROLES.filter(r => !roles.includes(r)).map(r => (
              <MenuItem key={r} value={r}>{r}</MenuItem>
            ))}
          </Select>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
          <Typography variant="caption" color="text.secondary">Density</Typography>
          <ToggleButtonGroup size="small" value={trackHeight} exclusive onChange={(e,v)=>{ if (v) setTrackHeight(v); }}>
            <ToggleButton value={64}>Compact</ToggleButton>
            <ToggleButton value={96}>Cozy</ToggleButton>
            <ToggleButton value={140}>Comfy</ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{ width: 140, display: { xs: 'none', md: 'block' } }}>
            <Slider size="small" min={48} max={160} step={8} value={trackHeight} onChange={(e,v)=>setTrackHeight(v)} />
          </Box>
        </Stack>
        <Button variant="contained" startIcon={<Save />} onClick={saveAll} disabled={saving}>
          {saving ? 'Saving...' : 'Save Requirements'}
        </Button>
      </Stack>

      <Box sx={{ overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        {/* Header */}
        <Box sx={{ display: 'grid', gridTemplateColumns: `220px repeat(${DAYS.length}, minmax(220px, 1fr))`, position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ p: 1, borderRight: '1px solid', borderColor: 'divider', fontWeight: 600, color: 'text.secondary' }}>Role</Box>
          {DAYS.map(d => (
            <Box key={d.id} sx={{ p: 1, textAlign: 'center', borderRight: '1px solid', borderColor: 'divider' }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{d.name}</Typography>
            </Box>
          ))}
        </Box>

        {/* Rows */}
        {roles.map(role => (
          <Box key={role} sx={{ display: 'grid', gridTemplateColumns: `220px repeat(${DAYS.length}, minmax(220px, 1fr))`, borderBottom: '1px solid', borderColor: 'divider' }}>
            {/* Role label */}
            <Box sx={{ p: 1, borderRight: '1px solid', borderColor: 'divider' }}>
              <Stack spacing={0.5}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{role}</Typography>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title={`Remove ${role}`}>
                      <IconButton size="small" color="error" onClick={()=>removeRole(role)}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
                {(() => {
                  const sum = hudSummary[role];
                  if (!sum) return null;
                  const weeklyHrsNum = (sum.weeklyMinutes || 0) / 60;
                  const weeklyHrs = weeklyHrsNum.toFixed(1).replace(/\.0$/, '');
                  const daysWithValues = DAYS.filter(d => sum.perDay?.[d.id]);
                  const maxSnippets = 2;
                  const snippets = daysWithValues.slice(0, maxSnippets).map(d => {
                    const h = sum.perDay[d.id];
                    const hrs = (h.mins/60).toFixed(1).replace(/\.0$/, '');
                    return `${d.name}: ${h.heads} ${h.heads===1?'person':'people'}, ${hrs}h`;
                  });
                  const extra = Math.max(0, daysWithValues.length - maxSnippets);
                  if (weeklyHrsNum === 0 && snippets.length === 0) return null;
                  const fullList = daysWithValues.map(d => {
                    const h = sum.perDay[d.id];
                    const hrs = (h.mins/60).toFixed(1).replace(/\.0$/, '');
                    return `${d.name}: ${h.heads} ${h.heads===1?'person':'people'}, ${hrs}h`;
                  }).join(', ');
                  return (
                    <Tooltip title={`${weeklyHrs}h/week — ${fullList}`} arrow>
                      <Typography variant="caption" color="text.secondary">
                        {weeklyHrs}h/week{snippets.length ? ` — ` + snippets.join(', ') : ''}{extra ? `, +${extra} more` : ''}
                      </Typography>
                    </Tooltip>
                  );
                })()}
              </Stack>
            </Box>
            {/* Day cells */}
            {DAYS.map(d => (
              <Box
                key={d.id}
                sx={{ p: 1, borderRight: '1px solid', borderColor: 'divider', position: 'relative', userSelect: 'none', cursor: 'pointer' }}
                onClick={(e)=>{ e.stopPropagation(); setCreatePopover({ open: true, anchorEl: e.currentTarget, role, dayId: d.id, start: '09:00', end: '17:00', headcount: 1 }); }}
              >
                <Stack spacing={0.5} sx={{ minHeight: trackHeight, border: '1px dashed', borderColor: 'divider', borderRadius: 1, p: 0.5, bgcolor: 'background.paper' }}>
                  {(grid[role]?.[d.id] || []).map((b, idx) => (
                    <Tooltip key={b.id ?? `new-${idx}`} title={`${to12h(b.startTime)}–${to12h(b.endTime)} · x${b.headcount}`} arrow>
                      <Paper variant="outlined" sx={{ p: 0.25, px: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, cursor: 'pointer', bgcolor: 'background.paper' }}
                        onClick={(e)=>{ e.stopPropagation(); setEditPopover({ open: true, anchorEl: e.currentTarget, role, dayId: d.id, idx, start: b.startTime, end: b.endTime, headcount: b.headcount||1 }); }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.15 }}>{to12hCompact(b.startTime)}–{to12hCompact(b.endTime)}</Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip size="small" label={`x${b.headcount}`} color="primary" />
                          <Tooltip title="Edit"><IconButton size="small" onClick={(e)=>{ e.stopPropagation(); setEditPopover({ open: true, anchorEl: e.currentTarget, role, dayId: d.id, idx, start: b.startTime, end: b.endTime, headcount: b.headcount||1 }); }}><EditOutlined fontSize="small"/></IconButton></Tooltip>
                          <Tooltip title="Remove"><IconButton size="small" color="error" onClick={(e)=>{ e.stopPropagation(); removeBlock(role, d.id, idx); }}><DeleteOutline fontSize="small"/></IconButton></Tooltip>
                        </Stack>
                      </Paper>
                    </Tooltip>
                  ))}
                </Stack>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Button size="small" variant="outlined" startIcon={<Add />} onClick={()=>addBlock(role, d.id)}>Add</Button>
                </Stack>
              </Box>
            ))}
          </Box>
        ))}
      </Box>

      {/* Create Popover */}
      <Popover open={createPopover.open} anchorEl={createPopover.anchorEl} onClose={()=>setCreatePopover({ ...createPopover, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
        <Box sx={{ p: 2, minWidth: 260 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Add Requirement</Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="create-start-label">Start</InputLabel>
              <Select labelId="create-start-label" label="Start" value={createPopover.start}
                onChange={(e)=>setCreatePopover(cp=>({ ...cp, start: e.target.value }))}>
                {TIME_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="create-end-label">End</InputLabel>
              <Select labelId="create-end-label" label="End" value={createPopover.end}
                onChange={(e)=>setCreatePopover(cp=>({ ...cp, end: e.target.value }))}>
                {TIME_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <TextField size="small" type="number" label="Headcount" value={createPopover.headcount} onChange={(e)=>setCreatePopover(cp=>({ ...cp, headcount: Math.max(1, Number(e.target.value||1)) }))} sx={{ width: 140, mb: 1 }} />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={()=>setCreatePopover({ ...createPopover, open: false })}>Cancel</Button>
            <Button variant="contained" onClick={()=>{
              const { role, dayId, start, end, headcount } = createPopover;
              if (!role || dayId==null) return;
              setGrid(prev => ({ ...prev, [role]: { ...(prev[role]||{}), [dayId]: [ ...(prev[role]?.[dayId]||[]), { startTime: start, endTime: end, headcount: Number(headcount)||1 } ] } }));
              setCreatePopover({ ...createPopover, open: false });
            }}>Add</Button>
          </Stack>
        </Box>
      </Popover>

      {/* Edit Popover */}
      <Popover open={editPopover.open} anchorEl={editPopover.anchorEl} onClose={()=>setEditPopover({ ...editPopover, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
        <Box sx={{ p: 2, minWidth: 280 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Edit Requirement</Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="edit-start-label">Start</InputLabel>
              <Select labelId="edit-start-label" label="Start" value={editPopover.start}
                onChange={(e)=>setEditPopover(ep=>({ ...ep, start: e.target.value }))}>
                {TIME_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="edit-end-label">End</InputLabel>
              <Select labelId="edit-end-label" label="End" value={editPopover.end}
                onChange={(e)=>setEditPopover(ep=>({ ...ep, end: e.target.value }))}>
                {TIME_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <TextField size="small" type="number" label="Headcount" value={editPopover.headcount} onChange={(e)=>setEditPopover(ep=>({ ...ep, headcount: Math.max(1, Number(e.target.value||1)) }))} sx={{ width: 140 }} />
            <Tooltip title="Copy across week"><IconButton onClick={()=>{ const { role, dayId, idx } = editPopover; copyAcrossWeek(role, dayId, idx); }}><ContentCopy/></IconButton></Tooltip>
          </Stack>
          <Stack direction="row" spacing={1} justifyContent="space-between">
            <Button color="error" onClick={()=>{ const { role, dayId, idx } = editPopover; removeBlock(role, dayId, idx); setEditPopover({ ...editPopover, open: false }); }}>Remove</Button>
            <Box>
              <Button onClick={()=>setEditPopover({ ...editPopover, open: false })} sx={{ mr: 1 }}>Cancel</Button>
              <Button variant="contained" onClick={()=>{
                const { role, dayId, idx, start, end, headcount } = editPopover;
                setGrid(prev => {
                  const arr = prev[role][dayId] || [];
                  const next = arr.map((b,i)=> i===idx ? { ...b, startTime: start, endTime: end, headcount: Number(headcount)||1 } : b);
                  return { ...prev, [role]: { ...prev[role], [dayId]: next } };
                });
                setEditPopover({ ...editPopover, open: false });
              }}>Save</Button>
            </Box>
          </Stack>
        </Box>
      </Popover>
    </Paper>
  );
}
